'use client'

import React, { useState } from 'react'
import { Plus, GripVertical } from 'lucide-react'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses } from '../ProcessedEntry/DomainEntry/processedTypes'
import { SIBS_WIDGET_LIBRARY, type SibsWidgetDefinition } from './widgetLibrary'

// Namespaced so a stray external drag (e.g. dragging text into the browser)
// can't accidentally add a widget via the drop zone below.
const DND_MIME = 'application/x-sibs-widget-id'

export function WidgetLibraryPicker({
  theme,
  onAdd,
  library = SIBS_WIDGET_LIBRARY,
  colSpanClassName = 'col-span-full',
}: {
  theme: ProcessedTheme
  onAdd: (widgetId: string) => void
  /** Defaults to the full SIBS library — pass WORKBENCH_WIDGET_LIBRARY (or another subset) for other grids. */
  library?: SibsWidgetDefinition[]
  /** Must always make this row span the full width of whatever grid it's dropped into. col-span-full does that for any column count — the detail grids step between 1, 2 and N columns with the shell's width (see detailGridCols in EntryDetailPanel), so a fixed span would overflow the narrower steps. */
  colSpanClassName?: string
}) {
  const c = watchClasses(theme)
  const [open, setOpen] = useState(false)
  const [dragOver, setDragOver] = useState(false)

  return (
    <div className={`${colSpanClassName} flex flex-col gap-2`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 self-start rounded border px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors hover:text-signal ${c.line} ${c.faint}`}
      >
        <Plus className="h-3 w-3" />
        Add widget
      </button>

      {open && (
        <>
          <div className="flex flex-wrap gap-2">
            {library.map((def) => (
              <div
                key={def.id}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData(DND_MIME, def.id)
                  e.dataTransfer.effectAllowed = 'copy'
                }}
                onClick={() => onAdd(def.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onAdd(def.id)
                  }
                }}
                className={`flex w-56 cursor-grab select-none items-start gap-2 border px-3 py-2 transition-colors hover:border-signal/60 active:cursor-grabbing ${c.line} ${c.panel}`}
              >
                <GripVertical className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${c.faint}`} />
                <div className="min-w-0">
                  <div className={`font-mono text-[10px] uppercase tracking-widest ${c.label}`}>{def.label}</div>
                  <div className={`mt-0.5 text-[11px] leading-snug ${c.faint}`}>{def.description}</div>
                </div>
              </div>
            ))}
          </div>

          <div
            onDragOver={(e) => {
              e.preventDefault()
              e.dataTransfer.dropEffect = 'copy'
            }}
            onDragEnter={() => setDragOver(true)}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault()
              setDragOver(false)
              const id = e.dataTransfer.getData(DND_MIME)
              if (id) onAdd(id)
            }}
            className={`flex h-16 items-center justify-center border-2 border-dashed font-mono text-[10px] uppercase tracking-widest transition-colors ${
              dragOver ? 'border-signal bg-signal-surface/60 text-signal' : `${c.line} ${c.faint}`
            }`}
          >
            Drag a widget here to add it
          </div>
        </>
      )}
    </div>
  )
}
